import { useState, memo, Suspense } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, SkeletonLoader } from '@frontend/design-system';
import { CallGroupPanel } from './call-group-queue-charts/call-group-panel';
import { CallQueuesPanel } from './call-group-queue-charts/call-queues-panel';
import { PhoneTreesCallVolume } from './charts/phone-trees-call-volume';

export const ROUTING_ANALYTICS_ID = 'routing-analytics' as const;
type ViewState = 'all' | 'phone-trees' | 'groups' | 'queues';

const MemoizedCallGroupPanel = memo(CallGroupPanel);
const MemoizedCallQueuesPanel = memo(CallQueuesPanel);
const MemoizedPhoneTreesCallVolume = memo(PhoneTreesCallVolume);

const PanelSkeleton = () => (
  <div style={{ padding: '16px' }}>
    <SkeletonLoader height={300} width='100%' />
  </div>
);

export const RoutingAnalyticsPanel = () => {
  const [view, setView] = useState<ViewState>('all');
  const { t } = useTranslation('analytics-phone');
  const handleViewChange = (newView: ViewState) => {
    setView(newView);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
      <div css={{ display: 'flex', gap: theme.spacing(1), padding: theme.spacing(0.5) }}>
        <Chip.SwitchFilter selected={view === 'all'} onClick={() => handleViewChange('all')}>
          {t('All')}
        </Chip.SwitchFilter>
        <Chip.SwitchFilter
          selected={view === 'phone-trees'}
          onClick={() => handleViewChange('phone-trees')}
          trackingId={`${ROUTING_ANALYTICS_ID}-phonetreesonly`}
        >
          {t('Phone Trees')}
        </Chip.SwitchFilter>
        <Chip.SwitchFilter
          selected={view === 'groups'}
          onClick={() => handleViewChange('groups')}
          trackingId={`${ROUTING_ANALYTICS_ID}-groupsonly`}
        >
          {t('Groups')}
        </Chip.SwitchFilter>
        <Chip.SwitchFilter
          selected={view === 'queues'}
          onClick={() => handleViewChange('queues')}
          trackingId={`${ROUTING_ANALYTICS_ID}-queuesonly`}
        >
          {t('Queues')}
        </Chip.SwitchFilter>
      </div>

      <Suspense fallback={<PanelSkeleton />}>
        {(view === 'all' || view === 'phone-trees') && <MemoizedPhoneTreesCallVolume />}

        {(view === 'all' || view === 'groups') && <MemoizedCallGroupPanel />}

        {(view === 'all' || view === 'queues') && <MemoizedCallQueuesPanel />}
      </Suspense>
    </div>
  );
};
